import { useEffect, useState } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { debounce } from "lodash-es"
import { FormLabel, Select } from "@oysterhr/ui"
import { Engagement } from "@oysterhr/schema"
import { useGetCompanyEngagements } from "../../../hooks"
import { PayrollChangeFormValues } from "../types"
import { idToStrongId } from "@oysterhr/common"

interface Props {
  setEngagement: (engagement?: Engagement) => void
  disabled?: boolean
  companyId: string
  externalEngagementId?: string
}

export const TeamMemberAsyncSelect = ({
  setEngagement,
  externalEngagementId,
  companyId,
  disabled = false,
}: Props) => {
  const { t } = useTranslation("Payroll")
  const { control, setValue } = useFormContext<PayrollChangeFormValues>()
  const [didSeachTheExternalEngagementId, setDidSeachTheExternalEngagementId] = useState(false)
  const { data, refetch, loading } = useGetCompanyEngagements({
    companyId,
    query: externalEngagementId ? idToStrongId(externalEngagementId) : undefined,
  })
  const engagements = data?.company?.engagements?.collection || []

  const handleSearch = debounce(async (e: React.ChangeEvent<HTMLInputElement>) => {
    refetch({ query: e.target.value })
  }, 500)

  useEffect(() => {
    if (
      !externalEngagementId ||
      didSeachTheExternalEngagementId ||
      !data?.company?.engagements?.collection
    )
      return
    setDidSeachTheExternalEngagementId(true)
    setValue("selectedEngagementId", idToStrongId(externalEngagementId))
    setEngagement(
      data.company.engagements.collection.find(
        ({ id }) => idToStrongId(id) === idToStrongId(externalEngagementId),
      ),
    )
  }, [
    data?.company?.engagements?.collection,
    didSeachTheExternalEngagementId,
    externalEngagementId,
    setEngagement,
    setValue,
  ])

  return (
    <Controller
      name="selectedEngagementId"
      control={control}
      render={({ field: { onChange, ref: _, ...field }, fieldState: { error } }) => (
        <Select
          {...field}
          disabled={disabled || !engagements.length}
          label={<FormLabel label={t("form.label.teamMember")} />}
          placeholder={
            loading
              ? t("form.placeholders.loadingTeamMembers")
              : engagements.length
                ? t("form.placeholders.teamMember")
                : t("form.placeholders.noTeamMembers")
          }
          onChange={value => {
            if (!value) return
            onChange(value)
            setEngagement(engagements.find(({ id }) => idToStrongId(id) === value))
          }}
          error={error?.message}
        >
          <Select.Input
            key="search"
            isLoading={loading}
            placeholder={t("form.placeholders.searchTeamMembers")}
            onChange={handleSearch}
          />
          {engagements.map(({ id, name }) => (
            <Select.Option key={id} value={idToStrongId(id)}>
              {name}
            </Select.Option>
          ))}
        </Select>
      )}
    />
  )
}
